import type { MapIconFontDescriptor } from '@repo/types/maps/map-types';
import { getIconForGlyph } from '@repo/types/maps/map-utils';
import { getGameFontDescriptorUrl } from '../../game-map/hooks/fontUtils';

export interface GameFontIcon {
    id: string;
    name: string;
    label: string;
}

const gameFontIconsCache = new Map<string, Promise<GameFontIcon[]>>();
const GAME_FONT_ICON_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function isGameFontDescriptor(value: unknown, fontId: string): value is MapIconFontDescriptor {
    if (!value || typeof value !== 'object') return false;
    const descriptor = value as Partial<MapIconFontDescriptor>;
    if (
        descriptor.version !== 1 ||
        descriptor.font !== fontId ||
        !descriptor.icons ||
        typeof descriptor.icons !== 'object' ||
        Array.isArray(descriptor.icons)
    ) {
        return false;
    }
    return Object.entries(descriptor.icons).every(
        ([name, reference]) =>
            GAME_FONT_ICON_NAME_PATTERN.test(name) &&
            typeof reference === 'string' &&
            reference === `${fontId}:${name}`,
    );
}

export function loadGameFontIcons(fontId: string): Promise<GameFontIcon[]> {
    const cached = gameFontIconsCache.get(fontId);
    if (cached) return cached;

    const pending = fetch(getGameFontDescriptorUrl(fontId)).then(async response => {
        if (!response.ok) {
            throw new Error(`Failed to load game font descriptor: ${response.status}`);
        }
        const descriptor: unknown = await response.json();
        if (!isGameFontDescriptor(descriptor, fontId)) {
            throw new Error(`Invalid game font descriptor: ${fontId}`);
        }

        return Object.entries(descriptor.icons).map(([label, reference]) => ({
            id: getIconForGlyph(reference),
            name: reference,
            label,
        }));
    });
    gameFontIconsCache.set(fontId, pending);
    void pending.catch(() => {
        if (gameFontIconsCache.get(fontId) === pending) {
            gameFontIconsCache.delete(fontId);
        }
    });
    return pending;
}

export function invalidateGameFontIcons(fontId: string): void {
    gameFontIconsCache.delete(fontId);
}
